import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type FeatureFlag,
  type AdminTemplate,
  type AbuseReport,
  type EmailHealthResponse,
} from '../api/client';

// ─── Feature Flags ──────────────────────────────────────────────────────────

export function useFeatureFlags() {
  return useQuery<FeatureFlag[]>({
    queryKey: ['admin', 'feature-flags'],
    queryFn: () => api.admin.listFeatureFlags(),
  });
}

export function useCreateFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      description?: string;
      isEnabled?: boolean;
      targetPlans?: string[] | null;
    }) => api.admin.createFeatureFlag(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
    },
  });
}

export function useUpdateFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        name: string;
        description: string;
        isEnabled: boolean;
        targetPlans: string[] | null;
      }>;
    }) => api.admin.updateFeatureFlag(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
    },
  });
}

export function useDeleteFeatureFlag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.admin.deleteFeatureFlag(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'feature-flags'] });
    },
  });
}

// ─── Admin Templates ────────────────────────────────────────────────────────

export function useAdminTemplates() {
  return useQuery<AdminTemplate[]>({
    queryKey: ['admin', 'templates'],
    queryFn: () => api.admin.listTemplates(),
  });
}

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      name: string;
      category: string;
      htmlLayout: string;
      previewImageUrl: string;
      isDefault?: boolean;
      isActive?: boolean;
    }) => api.admin.createTemplate(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
  });
}

export function useUpdateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: Partial<{
        name: string;
        category: string;
        htmlLayout: string;
        previewImageUrl: string;
        isDefault: boolean;
        isActive: boolean;
      }>;
    }) => api.admin.updateTemplate(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
  });
}

export function useDeactivateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.admin.deactivateTemplate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'templates'] });
    },
  });
}

// ─── Abuse Reports ──────────────────────────────────────────────────────────

export function useAbuseReports(status?: string) {
  return useQuery<AbuseReport[]>({
    queryKey: ['admin', 'abuse-reports', status],
    queryFn: () => api.admin.listAbuseReports(status),
  });
}

export function useCreateAbuseReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: {
      hostId?: string;
      eventId?: string;
      reportType: string;
      description?: string;
    }) => api.admin.createAbuseReport(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'abuse-reports'] });
    },
  });
}

export function useResolveAbuseReport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, resolution }: { id: string; resolution: string }) =>
      api.admin.resolveAbuseReport(id, resolution),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'abuse-reports'] });
    },
  });
}

// ─── Email Health ───────────────────────────────────────────────────────────

export function useEmailHealth(days = 30) {
  return useQuery<EmailHealthResponse>({
    queryKey: ['admin', 'email-health', days],
    queryFn: () => api.admin.getEmailHealth(days),
    refetchInterval: 60_000,
  });
}

export function useRecordEmailMetrics() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.admin.recordEmailMetrics(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'email-health'] });
    },
  });
}
